import { create } from 'zustand';
import type { TestSession, EditorContent, Question } from '@/types';

interface TestSessionState {
    session: TestSession | null;
    question: Question | null;
    editorContent: EditorContent;
    timeRemaining: number; // seconds
    isSubmitting: boolean;
    isAutosaving: boolean;
    lastAutosave: Date | null;
    error: string | null;
}

interface TestSessionActions {
    setSession: (session: TestSession) => void;
    setQuestion: (question: Question) => void;
    updateEditorContent: (content: EditorContent) => void;
    setTimeRemaining: (seconds: number) => void;
    decrementTime: () => void;
    setSubmitting: (isSubmitting: boolean) => void;
    setAutosaving: (isAutosaving: boolean) => void;
    setLastAutosave: (date: Date) => void;
    setError: (error: string | null) => void;
    reset: () => void;
}

const initialState: TestSessionState = {
    session: null,
    question: null,
    editorContent: { html: '', text: '' },
    timeRemaining: 0,
    isSubmitting: false,
    isAutosaving: false,
    lastAutosave: null,
    error: null,
};

export const useTestSessionStore = create<TestSessionState & TestSessionActions>((set, get) => ({
    ...initialState,

    setSession: (session) => {
        set({ session });
        // Initialize time remaining from session
        if (session.start_time && session.time_limit_minutes) {
            const startTime = new Date(session.start_time);
            const endTime = new Date(startTime.getTime() + session.time_limit_minutes * 60 * 1000);
            const remaining = Math.max(0, Math.floor((endTime.getTime() - Date.now()) / 1000));
            set({ timeRemaining: remaining });
        }
    },

    setQuestion: (question) => set({ question }),

    updateEditorContent: (content) => set({ editorContent: content }),

    setTimeRemaining: (seconds) => set({ timeRemaining: seconds }),

    decrementTime: () => {
        const { timeRemaining } = get();
        if (timeRemaining > 0) {
            set({ timeRemaining: timeRemaining - 1 });
        }
    },

    setSubmitting: (isSubmitting) => set({ isSubmitting }),

    setAutosaving: (isAutosaving) => set({ isAutosaving }),

    setLastAutosave: (date) => set({ lastAutosave: date }),

    setError: (error) => set({ error }),

    reset: () => set(initialState),
}));
